<template>
  <div>
    <!-- ── Spread estimate ── -->
    <div class="card shadow-sm table-card mb-4" v-if="spread">
      <div class="form-card-header flex-wrap">
        <div class="section-number"><i class="fe fe-share-2" style="font-size: 12px;"></i></div>
        <div class="flex-grow-1"><h6 class="mb-0 font-weight-bold text-white">How far and how fast it is spreading</h6>
          <small style="color: rgba(255,255,255,.75);">{{ spread.headline }}</small></div>
        <span class="conf" :class="`conf--${g.confidence}`" :title="confidenceTitle">{{ g.confidence }} confidence</span>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-lg-3 col-sm-6" v-for="t in spreadTiles" :key="t.label">
            <div class="ins-tile">
              <p class="stat-label mb-1"><i :class="t.icon" class="mr-1"></i>{{ t.label }}</p>
              <div class="stat-value">{{ t.value }}</div>
              <p class="stat-sub">{{ t.sub }}</p>
            </div>
          </div>
        </div>
        <div v-if="spread.areas.newly_affected.length" class="small mb-2">
          <i class="fe fe-map-pin text-danger mr-1"></i><b>Newly affected:</b>
          <span v-for="n in spread.areas.newly_affected" :key="n" class="area-chip">{{ n }}</span>
        </div>
        <div v-if="!spread.population_available" class="alert alert-light border small mb-3">
          <i class="fe fe-info mr-1"></i>Population estimates need ward populations.
          <router-link to="/population">Add them on the Population page</router-link>; until then figures cover enrollees only.
        </div>
      </div>
      <div class="table-responsive" v-if="spread.lgas.length">
        <table class="table mb-0">
          <thead class="table-head"><tr>
            <th class="table-th">LGA</th><th class="table-th text-right">Sufferers in period</th><th class="table-th text-right">Per 1,000 enrollees</th>
            <th class="table-th text-right">Population</th><th class="table-th text-right">Estimated in population</th>
            <th class="table-th text-right">Last 28 days</th><th class="table-th text-right">28 days before</th><th class="table-th">Trend</th>
          </tr></thead>
          <tbody>
            <tr class="table-row" v-for="l in shownLgas" :key="l.id">
              <td class="font-weight-bold">{{ l.name }} <span v-if="l.newly_affected" class="sev sev-high ml-1">New</span></td>
              <td class="text-right">{{ n(l.sufferers) }}</td>
              <td class="text-right">{{ l.per_1000_enrollees === null ? "—" : l.per_1000_enrollees }}</td>
              <td class="text-right">{{ l.population === null ? "—" : n(l.population) }}</td>
              <td class="text-right">
                <span v-if="l.estimated_sufferers !== null">~{{ n(l.estimated_sufferers) }}</span><span v-else>—</span>
                <i v-if="l.estimate_basis === 'state'" class="fe fe-info text-muted ml-1" title="Few enrollees here: estimated at the state rate"></i>
              </td>
              <td class="text-right font-weight-bold">{{ l.last_28_days }}</td>
              <td class="text-right text-muted">{{ l.previous_28_days }}</td>
              <td><span class="trend" :class="`trend--${l.trend}`"><i :class="trendIcon(l.trend)" class="mr-1"></i>{{ trendLabel(l.trend) }}</span></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="card-footer bg-white d-flex flex-wrap justify-content-between align-items-center" style="gap: 8px;">
        <small class="text-muted">Estimates assume enrollees fall ill at the same rate as everyone in their LGA, and count recorded diagnoses only.
          LGAs with fewer than 100 enrollees use the state rate.</small>
        <button v-if="spread.lgas.length > 8" class="btn btn-sm btn-link p-0" @click="allLgas = !allLgas">{{ allLgas ? "Show fewer" : `Show all ${spread.lgas.length} LGAs` }}</button>
      </div>
    </div>

    <div class="row">
      <!-- ── Seasons ── -->
      <div class="col-lg-6">
        <div class="card shadow-sm table-card mb-4">
          <div class="form-card-header"><div class="section-number"><i class="fe fe-sun" style="font-size: 12px;"></i></div>
            <div class="flex-grow-1"><h6 class="mb-0 font-weight-bold text-white">Season it is most likely</h6>
              <small style="color: rgba(255,255,255,.75);">Last 3 years · 1.0× = an average month</small></div></div>
          <div class="card-body">
            <p v-if="likely" class="mb-3 insight-line">
              Most likely in the <b>{{ likely.label.toLowerCase() }}</b> ({{ likely.months }}): {{ likely.share_pct }}% of sufferers,
              <b>{{ likely.index }}×</b> an average month.
            </p>
            <p v-else class="mb-3 insight-line text-muted">{{ seasons.some((x) => x.index !== null) ? "No season stands out: about the same all year." : "Not enough history yet to tell." }}</p>
            <div class="season" v-for="x in seasons" :key="x.key" :class="{ 'season--likely': likely && likely.key === x.key }">
              <div class="d-flex justify-content-between align-items-baseline">
                <span class="font-weight-bold">{{ x.label }} <small class="text-muted">{{ x.months }}</small>
                  <span v-if="x.current" class="now-chip">now</span></span>
                <span class="small text-muted">{{ x.share_pct === null ? "—" : x.share_pct + "% of the year" }}</span>
              </div>
              <div class="season-bar" :title="`${x.average_per_month} sufferers a month on average`">
                <span :style="{ width: Math.min(100, (x.index || 0) / maxSeasonIndex * 100) + '%' }"></span>
                <i class="season-avg" :style="{ left: Math.min(100, 1 / maxSeasonIndex * 100) + '%' }" title="Average month"></i>
              </div>
              <small class="text-muted">{{ x.index === null ? "—" : `${x.index}× · about ${x.average_per_month} sufferers a month` }}</small>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Who is at risk ── -->
      <div class="col-lg-6" v-if="risk">
        <div class="card shadow-sm table-card mb-4">
          <div class="form-card-header"><div class="section-number"><i class="fe fe-shield" style="font-size: 12px;"></i></div>
            <div class="flex-grow-1"><h6 class="mb-0 font-weight-bold text-white">Who is at risk</h6>
              <small style="color: rgba(255,255,255,.75);">Sufferers per 1,000 enrollees of each group · overall {{ risk.overall_per_1000 }}</small></div></div>
          <div class="card-body">
            <div class="d-flex flex-wrap mb-2" style="gap: 8px;">
              <div v-for="r in riskCallouts" :key="r.title" class="risk-callout" :class="{ 'risk-callout--none': !r.row }">
                <p class="stat-label mb-1">{{ r.title }}</p>
                <template v-if="r.row"><div class="font-weight-bold">{{ r.row.key }}</div>
                  <small>{{ r.row.relative_risk }}× the overall rate · {{ r.row.per_1000 }} per 1,000</small></template>
                <div v-else class="small text-muted">No group stands out</div>
              </div>
            </div>
            <apexchart type="bar" :height="Math.max(220, riskRows.length * 28)" :options="riskOptions" :series="[{ name: 'Relative risk', data: riskRows.map((r) => r.relative_risk || 0) }]" :key="'r' + k" />
            <small class="text-muted d-block">1.0× = the overall rate. Groups marked higher risk are ≥ 1.2× with at least 3 sufferers.
              <span v-if="risk.unknown.sex_not_set || risk.unknown.age_unknown">{{ risk.unknown.sex_not_set }} sufferers without sex and {{ risk.unknown.age_unknown }} without date of birth are left out.</span></small>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/** Spread estimates, seasons and risk groups of a tracked disease (diseases/{id}/summary: spread, seasonality, risk_groups). */
export default {
  props: { s: { type: Object, required: true }, k: { type: Number, default: 0 } },
  data() { return { allLgas: false }; },
  computed: {
    spread() { return this.s.spread; },
    g() { return this.spread.growth; },
    risk() { return this.s.risk_groups; },
    seasons() { return this.s.seasonality.seasons || []; },
    likely() { return this.s.seasonality.likely_season; },
    maxSeasonIndex() { return Math.max(1.5, ...this.seasons.map((x) => x.index || 0)); },
    shownLgas() { return this.allLgas ? this.spread.lgas : this.spread.lgas.slice(0, 8); },
    confidenceTitle() { return `Based on ${this.g.last_14_days + this.g.previous_14_days} sufferers in the last 4 weeks; fewer than 10 gives low confidence, 30 or more high.`; },
    spreadTiles() {
      const e = this.spread.estimate;
      const g = this.g;
      const change = g.weekly_change_pct === null ? (g.last_14_days ? "new this fortnight" : "no recent sufferers")
        : `${g.weekly_change_pct > 0 ? "+" : ""}${g.weekly_change_pct}% a week`;
      return [
        {
          label: "Estimated in population", icon: "fe fe-users",
          value: e.estimated_sufferers === null ? "—" : "~" + this.n(e.estimated_sufferers),
          sub: e.estimated_sufferers === null ? `${this.n(e.sufferers_counted)} enrollees counted` : `range ${this.n(e.low)}–${this.n(e.high)} · ${this.n(e.per_100k_population)} per 100,000 · from ${this.n(e.sufferers_counted)} enrollees`,
        },
        {
          label: "Growth (last 2 weeks)", icon: g.direction === "falling" ? "fe fe-trending-down" : "fe fe-trending-up",
          value: change,
          sub: g.doubling_days ? `doubling about every ${g.doubling_days} days` : g.halving_days ? `halving about every ${g.halving_days} days` : `${g.last_14_days} against ${g.previous_14_days} the 2 weeks before`,
        },
        {
          label: "Expected next 28 days", icon: "fe fe-fast-forward",
          value: this.n(g.projected_next_28_days) + " enrollees",
          sub: g.projected_in_population === null ? "if the current pace holds" : `~${this.n(g.projected_in_population)} people in the population, if the pace holds`,
        },
        {
          label: "LGAs affected (28 days)", icon: "fe fe-map",
          value: `${this.spread.areas.affected_last_28_days}${this.spread.areas.total_lgas ? " of " + this.spread.areas.total_lgas : ""}`,
          sub: `${this.spread.areas.affected_previous_28_days} in the 28 days before`,
        },
      ];
    },
    riskCallouts() {
      const m = this.risk.most_at_risk;
      return [{ title: "Most at risk", row: m.group }, { title: "Sex", row: m.sex }, { title: "Age group", row: m.age }];
    },
    riskRows() {
      return [...this.risk.gender, ...this.risk.age.filter((r) => r.enrollees > 0)];
    },
    riskOptions() {
      const rows = this.riskRows;
      return {
        chart: { toolbar: { show: false } },
        plotOptions: { bar: { horizontal: true, barHeight: "62%", borderRadius: 4, distributed: true } },
        colors: rows.map((r) => (r.higher_risk ? "#dc2626" : "#93c5fd")),
        xaxis: { categories: rows.map((r) => r.key), labels: { formatter: (v) => `${v}×` } },
        legend: { show: false }, dataLabels: { enabled: true, formatter: (v) => `${v}×`, style: { colors: ["#1e293b"] }, offsetX: 18 },
        grid: { borderColor: "#eef2f7" },
        annotations: { xaxis: [{ x: 1, borderColor: "#64748b", strokeDashArray: 4, label: { text: "overall", style: { background: "#64748b", color: "#fff" } } }] },
        tooltip: { y: { formatter: (v, o) => { const r = rows[o.dataPointIndex]; return `${v}× · ${r.sufferers} of ${this.n(r.enrollees)} enrollees (${r.per_1000} per 1,000)${r.higher_risk ? " · higher risk" : ""}`; } } },
      };
    },
  },
  methods: {
    n(v) { return v === null || v === undefined ? "—" : Math.round(v).toLocaleString(); },
    trendLabel(t) { return { rising: "Rising", falling: "Falling", steady: "Steady", new: "New", none: "—" }[t] || t; },
    trendIcon(t) { return { rising: "fe fe-arrow-up-right", falling: "fe fe-arrow-down-right", steady: "fe fe-arrow-right", new: "fe fe-plus" }[t] || ""; },
  },
};
</script>

<style scoped>
@import "../../reports/reports.css";
.ins-tile { border: 1px solid #e9ecef; border-radius: 12px; padding: 12px 14px; margin-bottom: 12px; height: calc(100% - 12px); background: #fbfdff; }
.conf { font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; padding: 3px 10px; border-radius: 20px; background: rgba(255,255,255,0.18); color: #fff; }
.conf--high { background: #dcfce7; color: #166534; }
.conf--medium { background: #fef3c7; color: #92400e; }
.area-chip { display: inline-block; background: #fef2f2; color: #b91c1c; border-radius: 20px; padding: 1px 8px; margin: 0 4px 4px 0; font-weight: 600; font-size: 0.75rem; }
.trend { display: inline-flex; align-items: center; font-size: 0.72rem; font-weight: 700; padding: 2px 8px; border-radius: 20px; background: #f1f5f9; color: #475569; }
.trend--rising { background: #fee2e2; color: #b91c1c; }
.trend--falling { background: #dcfce7; color: #15803d; }
.trend--new { background: #fef3c7; color: #b45309; }
.insight-line { font-size: 0.92rem; color: #1e293b; }
.season { padding: 8px 10px; border-radius: 10px; margin-bottom: 6px; }
.season--likely { background: #fff7ed; box-shadow: inset 0 0 0 1px #fed7aa; }
.season-bar { position: relative; height: 8px; background: #eef2f7; border-radius: 4px; margin: 6px 0 2px; }
.season-bar span { display: block; height: 100%; border-radius: 4px; background: #2563eb; }
.season--likely .season-bar span { background: #ea580c; }
.season-avg { position: absolute; top: -3px; width: 2px; height: 14px; background: #64748b; }
.now-chip { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; background: #1e3a5f; color: #fff; border-radius: 20px; padding: 1px 6px; margin-left: 4px; vertical-align: middle; }
.risk-callout { flex: 1 1 140px; border: 1px solid #fecaca; background: #fef2f2; border-radius: 10px; padding: 8px 12px; color: #7f1d1d; }
.risk-callout--none { border-color: #e2e8f0; background: #f8fafc; color: #475569; }
</style>
